import { useEffect, useState } from "react";
import { getInvoicesByArtist } from "../../../api/invoicesApi";
import PageLoader from "../../../components/pageLoader";
import Toast from "../../../components/toast";
import ToastContainer from "../../../components/toastContainer";
import { updateArtist } from "../../../api/artistApi";
import ArtistFormModal from "../AritstFormModal";
import { PencilIcon, TrashIcon } from "@heroicons/react/outline";

const DetailsTab = ({ artist, onRefreshArtist }) => {
  const [toast, setToast] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const formatAddress = (address) => {
    if (!address) return "-";

    const parts = [
      address.address_1,
      address.address_2,
      address.address_3,
      address.city,
      address.county,
      address.country,
      address.postcode,
    ];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const handleUpdateArtist = async (formData) => {
    try {
      setSaving(true);

      const payload = {
        real_name: formData.real_name,
        stage_name: formData.stage_name,
        email: formData.email,
        phone: formData.phone,
        dob: formData.dob,
        closest_airports: formData.closestAirports.map((name) => ({ name })),
        next_of_kin_name: formData.next_of_kin_name,
        next_of_kin_phone: formData.next_of_kin_phone,
        address: {
          address_1: formData.addressLine1,
          address_2: formData.addressLine2,
          address_3: formData.addressLine3,
          city: formData.city,
          county: formData.county,
          country: formData.country,
          postcode: formData.postcode,
        },
      };

      await updateArtist(artist.id, payload);

      setToast({
        type: "success",
        message: "Company details updated successfully",
      });

      setShowEditModal(false);

      // refresh artist data
      onRefreshArtist(); // pass this from parent
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm p-4">
      {/* Left – Artist Info */}
      <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-semibold">Artist Details</h3>
          <button
            onClick={() => setShowEditModal(true)}
            className="text-xs text-pink-500 hover:underline"
          >
            Edit
          </button>
        </div>
        <Info label="Stage Name" value={artist.stage_name} />
        <Info label="Real Name" value={artist.real_name} />
        <Info label="Phone No" value={artist.phone} />
        <Info label="Email Address" value={artist.email} />
        <Info label="Date of Birth" value={artist.dob} />
        <Info label="Next of Kin Name" value={artist.next_of_kin_name} />
        <Info label="Next of Kin Phone No" value={artist.next_of_kin_phone} />
        <Info label="Home Address" value={formatAddress(artist.address)} />
        <Info
          label="Closest Airports"
          value={artist.closest_airports?.map((a) => a.name).join(", ")}
        />
      </div>

      {/* Middle – Musical Production */}
      {/* Middle – Musical Production & Recorded Sets */}
      <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-4">
          Musical Production & Recorded Sets
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-slate-400">
              <tr>
                <th className="text-left py-2">Item</th>
                <th className="text-left py-2">Date Added</th>
                <th className="text-left py-2">Link</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {(artist.musical_production_and_recorded_sets || []).map(
                (item) => (
                  <tr
                    key={item.id}
                    className="border-b border-white/5 hover:bg-slate-800/40 transition"
                  >
                    <td className="py-2 text-white">{item.item || "-"}</td>

                    <td className="py-2 text-slate-400">
                      {item.date_added || "-"}
                    </td>

                    <td className="py-2">
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-pink-500 hover:underline"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>

                    <td className="py-2 text-right space-x-3">
                      <button className="text-slate-400 hover:text-white">
                        <PencilIcon className="w-4 h-4 inline" />
                      </button>
                      <button className="text-red-400 hover:text-red-500">
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ),
              )}

              {!artist.musical_production_and_recorded_sets?.length && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-slate-400">
                    No recorded sets available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right – Image + Logs */}
      <div className="space-y-4">
        <div className="bg-slate-900/80 border border-white/10 rounded-lg h-48 flex items-center justify-center">
          {artist.profile_picture ? (
            <img
              src={artist.profile_picture}
              alt={artist.stage_name}
              className="h-full w-full object-cover rounded-lg"
            />
          ) : (
            <span className="text-slate-500">No Image</span>
          )}
        </div>

        {/* Logs */}
        {/* Logs */}
        <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4 flex flex-col">
          <h3 className="text-white font-semibold mb-3">Activity Logs</h3>

          <div className="flex-1 overflow-y-auto pr-2 space-y-4 max-h-64">
            {artist.activity_logs?.length ? (
              artist.activity_logs.map((log) => (
                <div
                  key={log.id}
                  className="border-b border-white/10 pb-3 last:border-none"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">
                      {log.action}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatDate(log.date)}
                    </span>
                  </div>

                  <p className="text-sm text-slate-400 mt-1">
                    {log.description}
                  </p>

                  {Array.isArray(log.changes) && log.changes.length > 0 && (
                    <div className="mt-2 text-xs text-slate-400 space-y-1">
                      {log.changes.map((change, idx) => (
                        <div key={idx}>
                          <span className="text-slate-300">{change.field}</span>{" "}
                          changed from{" "}
                          <span className="text-red-400">
                            {change.old || "—"}
                          </span>{" "}
                          to{" "}
                          <span className="text-green-400">
                            {change.new || "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-slate-400 text-sm">
                No activity logs available.
              </p>
            )}
          </div>
        </div>
      </div>

      <ArtistFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateArtist}
        loading={saving}
        initialData={artist}
      />

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="mb-2">
    <p className="text-slate-400">{label}</p>
    <p className="text-white">{value || "-"}</p>
  </div>
);

export default DetailsTab;
